import { PercentageMetrics } from '../OrbusStreamNavigatorComponent/lib/PercentageMetrics';

export interface Capability {
  id: string;
  label: string;
  type?: string;
  description?: string;
  stageId?: string;
  stageName?: string;
  metrics?: number;
  metricsObj: PercentageMetrics;
}

export interface TransformedStage {
  id: string;
  label: string;
  description: string;
  sortOrder: number;
  type: string;
  metrics: PercentageMetrics;
  capabilities: Array<{
    id: string;
    name: string;
    timeDimension: string;
    type: string;
  }>;
}

/**
 * Calculate average percentage for all descendants of a specific type for a capability
 */
export const getAverageForDescendantType = (valueStream: any, capabilityId: string, descendantType: string): PercentageMetrics | null => {
  // Find all items that belong to this capability
  const capabilityItems: any[] = [];
  for (const lane of valueStream.lanes || []) {
    for (const stage of lane.stages || []) {
      for (const item of stage.items || []) {
        const capabilities = item.instanceOf || [];
        if (capabilities.some((cap: any) => cap.id === capabilityId)) {
          capabilityItems.push(item);
        }
      }
    }
  }

  // Now find all descendants of the specified type from these items
  const descendants: any[] = [];
  for (const item of capabilityItems) {
    if (descendantType === 'TEAM') {
      const teams = item.data?.delegatedToOrganisation || [];
      descendants.push(...teams);
    } else if (descendantType === 'APPLICATION_GROUP') {
      const apps = item.data?.deliveredByApplication || [];
      descendants.push(...apps);
    }
  }

  // For now, assume all descendants have random metrics (from API)
  // In a real implementation, these would come from the actual API data
  const childMetrics: PercentageMetrics[] = [];
  for (const descendant of descendants) {
    if (descendant) {
      // Use random metrics as placeholder (same as current implementation)
      childMetrics.push(new PercentageMetrics());
    }
  }

  if (childMetrics.length === 0) {
    return null;
  }

  return new PercentageMetrics(
    Math.round(childMetrics.reduce((sum, m) => sum + m.TimePercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.VolumePercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.QualityPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.SentimentPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.CostPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.RiskPercentage, 0) / childMetrics.length)
  );
};

/**
 * Calculate average percentage for LEAF nodes filtered by their subtype (Activity or Outcome)
 */
export const getAverageForLeafSubtype = (valueStream: any, capabilityId: string, subtype: string): PercentageMetrics | null => {
  // Find all items that belong to this capability
  const capabilityItems: any[] = [];
  for (const lane of valueStream.lanes || []) {
    for (const stage of lane.stages || []) {
      for (const item of stage.items || []) {
        const capabilities = item.instanceOf || [];
        if (capabilities.some((cap: any) => cap.id === capabilityId)) {
          capabilityItems.push(item);
        }
      }
    }
  }

  // Find LEAF nodes (Activities and Outcomes) from these items
  const leafNodes: any[] = [];
  for (const item of capabilityItems) {
    const enactedBy = item.data?.enactedBy || []; // Activities
    const outcomes = item.data?.capabilityDesiredOutcome || []; // Outcomes
    leafNodes.push(...enactedBy, ...outcomes);
  }

  // Filter by subtype
  const filteredLeafs = leafNodes.filter(leaf => {
    if (subtype === 'Activity') {
      return leaf; // All enactedBy are Activities
    } else if (subtype === 'Outcome') {
      return leaf; // All capabilityDesiredOutcome are Outcomes
    }
    return false;
  });

  // For now, assume all leaf nodes have random metrics
  const childMetrics: PercentageMetrics[] = [];
  for (const leaf of filteredLeafs) {
    if (leaf) {
      childMetrics.push(new PercentageMetrics());
    }
  }

  if (childMetrics.length === 0) {
    return null;
  }

  return new PercentageMetrics(
    Math.round(childMetrics.reduce((sum, m) => sum + m.TimePercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.VolumePercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.QualityPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.SentimentPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.CostPercentage, 0) / childMetrics.length),
    Math.round(childMetrics.reduce((sum, m) => sum + m.RiskPercentage, 0) / childMetrics.length)
  );
};

/**
 * Process capabilities data to calculate actual percentages using rollup logic
 */
export const processCapabilities = (capabilities: Omit<Capability, 'metricsObj'>[], valueStreamData: any): Capability[] => {
  return capabilities.map(capability => {
    // Find all descendants of different types for this capability
    const activityAvg = getAverageForLeafSubtype(valueStreamData, capability.id, 'Activity');
    const outcomeAvg = getAverageForLeafSubtype(valueStreamData, capability.id, 'Outcome');
    const appGroupAvg = getAverageForDescendantType(valueStreamData, capability.id, 'APPLICATION_GROUP');
    const teamAvg = getAverageForDescendantType(valueStreamData, capability.id, 'TEAM');

    // Collect all non-null averages
    const allAverages: PercentageMetrics[] = [];
    if (activityAvg) allAverages.push(activityAvg);
    if (outcomeAvg) allAverages.push(outcomeAvg);
    if (appGroupAvg) allAverages.push(appGroupAvg);
    if (teamAvg) allAverages.push(teamAvg);

    // Calculate final Capability percentage as average of the 4 child type averages
    let calculatedMetrics: PercentageMetrics;
    if (allAverages.length > 0) {
      calculatedMetrics = new PercentageMetrics(
        Math.round(allAverages.reduce((sum, m) => sum + m.TimePercentage, 0) / allAverages.length),
        Math.round(allAverages.reduce((sum, m) => sum + m.VolumePercentage, 0) / allAverages.length),
        Math.round(allAverages.reduce((sum, m) => sum + m.QualityPercentage, 0) / allAverages.length),
        Math.round(allAverages.reduce((sum, m) => sum + m.SentimentPercentage, 0) / allAverages.length),
        Math.round(allAverages.reduce((sum, m) => sum + m.CostPercentage, 0) / allAverages.length),
        Math.round(allAverages.reduce((sum, m) => sum + m.RiskPercentage, 0) / allAverages.length)
      );
    } else {
      // Fallback to random if no descendants found
      calculatedMetrics = new PercentageMetrics();
    }

    return {
      ...capability,
      metricsObj: calculatedMetrics
    };
  });
};

/**
 * Calculate stage metrics as average of all capability metrics within that stage
 */
export const processStageMetrics = (stages: Omit<TransformedStage, 'metrics'>[], capabilities: Capability[]): TransformedStage[] => {
  return stages.map(stage => {
    // Find all capabilities in this stage
    const stageCapabilities = capabilities.filter(cap =>
      cap.stageId === stage.id || cap.stageName === stage.label
    );

    if (stageCapabilities.length === 0) {
      // No capabilities found, use random metrics
      return {
        ...stage,
        metrics: new PercentageMetrics()
      };
    }

    // Calculate average of all capability metrics in this stage
    const capabilityMetrics = stageCapabilities.map(cap => cap.metricsObj);

    return {
      ...stage,
      metrics: new PercentageMetrics(
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.TimePercentage, 0) / capabilityMetrics.length),
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.VolumePercentage, 0) / capabilityMetrics.length),
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.QualityPercentage, 0) / capabilityMetrics.length),
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.SentimentPercentage, 0) / capabilityMetrics.length),
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.CostPercentage, 0) / capabilityMetrics.length),
        Math.round(capabilityMetrics.reduce((sum, m) => sum + m.RiskPercentage, 0) / capabilityMetrics.length)
      )
    };
  });
};
